<template>
  <component
    :is="getAs"
    ref="button"
    v-tooltip.bottom="tooltip"
    :class="getClasses"
    :disabled="props.disabled"
    :target="target"
    :type="getType"
    class="flex items-center"
    @click="onClick"
  >
    <div class="mx-auto">
      <div
        v-show="loading"
      >
        <stormy-spinner-small
          :size="6"
        />
      </div>
      <stormy-icon
        v-show="!loading"
        :name="props.icon"
        :class="iconClasses"
        :stroke-width="strokeWidth"
      />
    </div>
    <div v-if="hasSlot">
      <slot />
    </div>
  </component>
</template>
<script setup>
import { ref, computed, toRefs, useSlots } from 'vue'
import { useProp } from '@/composables/useProp.js'
import { useButton } from '@/composables/useButton.js'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

const emit = defineEmits(['click'])
const button = ref()

const hasSlot = computed(() => !!$slots.defaukt)
const $slots = useSlots()

const to = computed(() => {
  const newRoute = router.resolve(props.href)
  return newRoute.href
})

const onClick = () => {
  if (props.disabled) return
  if (props.loading) return
  if (props.href && to.value !== route.path) {
    router.push({ path: to.value })
  } else {
    emit('click')
  }
}

const props = defineProps({
  icon: useProp(String, '', true),
  loading: useProp(Boolean, false),
  iconClass: useProp(String, ''),
  target: useProp(String),
  disabled: useProp(Boolean),
  tooltip: useProp(String),
  active: useProp(Boolean, false),
  focusOnClick: useProp(Boolean, true),
  variant: useProp(String, 'outline'),
  type: useProp(String, 'button'),
  size: useProp(String, 'md'),
  href: useProp([String, Object], ''),
  tag: useProp(String, ''),
  strokeWidth: useProp(Number, 2)
})

const { active } = toRefs(props)

const getVariant = computed(() => active.value && props.variant !== 'iconbar' ? 'outline' : props.variant)

const realProps = computed(() => {
  const realProps = Object.assign({}, props)
  realProps.variant = getVariant.value
  return realProps
})

const getAs = computed(() => props.tag ? props.tag : props.href ? 'router-link' : 'button')
const getType = computed(() => getAs.value === 'button' ? 'button' : null)
const { getIconSize, getClasses } = useButton(realProps, getVariant)

const iconClasses = computed(() => props.iconClass + ' ' + getIconSize.value)

</script>
